<!-- 富文本编辑器组件 - PC 端，与小程序端 components/richTextEditor 能力对齐 -->
<!-- 仅支持：加粗 / 下划线 / 文字颜色（70 色固定色板），纯文本字数统计 + 清空 -->
<template>
  <div class="rich-editor-container">
    <div class="editor-box">
      <quill-editor
        ref="quillEditor"
        v-model="content"
        :options="editorOption"
        @ready="onEditorReady"
        @change="onEditorChange"
      />
    </div>
    <div class="editor-toolbar">
      <div class="toolbar-left">
        <span
          class="fmt-btn fmt-bold"
          title="加粗"
          @mousedown.prevent="handleFormat('bold')"
          >B</span
        >
        <span
          class="fmt-btn fmt-underline"
          title="下划线"
          @mousedown.prevent="handleFormat('underline')"
          >U</span
        >
        <span
          class="fmt-btn fmt-color"
          title="文字颜色"
          @mousedown.prevent="customColor"
        >
          A<span class="color-bar"></span>
        </span>
      </div>
      <div class="toolbar-right">
        <span class="count-text" :class="{ 'count-over': textCount > maxlength }"
          >{{ textCount }}/{{ maxlength }}</span
        >
        <span class="clear-btn" @mousedown.prevent="clear">清空</span>
      </div>
      <!-- 颜色面板：绝对定位在工具栏下方 -->
      <div v-if="showColor" class="color-panel">
        <div class="color-grid">
          <span
            v-for="(color, index) in colors"
            :key="index"
            class="color-item"
            :style="{ backgroundColor: color }"
            @mousedown.prevent="colorConfirmHandle(color)"
          ></span>
        </div>
      </div>
    </div>
    <!-- 全屏遮罩：点击关闭颜色面板 -->
    <div v-if="showColor" class="color-mask" @click="showColor = false"></div>
  </div>
</template>

<script>
import { quillEditor } from "vue-quill-editor";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import { normalizeHtml, isRichHtml, plainTextToHtml } from "./richTextUtils.js";

// 70 色固定色板，与小程序端 richTextEditor.vue 保持一致
const NOTICE_COLORS = Object.freeze([
  '#FFFFFF', '#000000', '#485269', '#0066FF', '#00CCFF', '#00CC66', '#FF3333', '#FF9900', '#FFCC00', '#CC33FF',
  '#F0F0F0', '#666666', '#E8E8E8', '#CCE5FF', '#CCF5FF', '#CCF5E6', '#FFE0E0', '#FFE8CC', '#FFF5CC', '#F5E0FF',
  '#D9D9D9', '#4D4D4D', '#D4D4D4', '#99C2FF', '#99E5FF', '#99E8D5', '#FFB3B3', '#FFD499', '#FFE699', '#E8C8FF',
  '#B3B3B3', '#333333', '#A8A8A8', '#66A3FF', '#66D4FF', '#66D4BE', '#FF8080', '#FFBA66', '#FFD966', '#D4A5FF',
  '#999999', '#1A1A1A', '#808080', '#3385FF', '#33C7FF', '#33C7A6', '#FF4D4D', '#FFA033', '#FFCC33', '#C785FF',
  '#7F7F7F', '#0D0D0D', '#666666', '#005CE6', '#00A8CC', '#00997A', '#CC0000', '#CC6600', '#CC9900', '#9900CC',
  '#666666', '#000000', '#4D4D4D', '#0047B3', '#007A99', '#007A5D', '#990000', '#994D00', '#997300', '#730099'
]);

export default {
  name: "RichTextEditor",
  components: {
    quillEditor,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "请输入...",
    },
    maxlength: {
      type: Number,
      default: 300,
    },
  },
  data() {
    return {
      quill: null,
      content: "",
      lastEmittedHtml: "",
      textCount: 0,
      showColor: false,
      editorOption: {
        theme: "snow",
        placeholder: this.placeholder,
        modules: {
          toolbar: false,
        },
      },
      colors: NOTICE_COLORS,
    };
  },
  watch: {
    value: {
      handler(newVal) {
        const normalized = normalizeHtml(newVal);
        if (normalized === this.lastEmittedHtml) return;
        // 外部回填内容：旧纯文本自动 \n -> <br>
        this.content = isRichHtml(normalized)
          ? normalized
          : plainTextToHtml(normalized);
      },
      immediate: true,
    },
  },
  methods: {
    onEditorReady(quill) {
      this.quill = quill;
      // 初始回填内容在 text-change 监听注册前粘贴（vue-quill-editor 实现），
      // 需在 ready 时补一次字数统计，与小程序端 setContents 回调里 updateCount 对齐
      this.updateCount();
    },
    onEditorChange({ html, text }) {
      const normalized = normalizeHtml(html);
      this.lastEmittedHtml = normalized;
      this.$emit("input", normalized);
      this.updateCount(text);
    },
    // 纯文本字数统计：去掉结尾换行，与小程序端口径一致
    updateCount(text) {
      const source =
        text !== undefined ? String(text || "") : this.quill.getText();
      const count = source.replace(/\n+$/, "").length;
      if (count !== this.textCount) {
        this.textCount = count;
        this.$emit("count-change", count);
      }
    },
    handleFormat(name) {
      if (!this.quill) return;
      const current = this.quill.getFormat();
      this.quill.format(name, !current[name]);
    },
    customColor() {
      if (!this.quill) return;
      this.showColor = true;
    },
    colorConfirmHandle(color) {
      this.showColor = false;
      if (!this.quill) return;
      this.quill.format("color", color);
    },
    clear() {
      if (!this.quill || !this.content) return;
      this.quill.setText("");
    },
  },
};
</script>

<style lang="scss" scoped>
.rich-editor-container {
  background: #ffffff;
  border: 1px solid #dcdde0;
  border-radius: 4px;
  padding: 7px 8px 5px;
  box-sizing: border-box;
}

.editor-box {
  width: 100%;
  position: relative;

  ::v-deep .ql-container.ql-snow {
    border: none;
  }

  ::v-deep .ql-editor {
    min-height: 120px;
    // 限制最大高度，内容超出时编辑器内部滚动，避免撑大弹层
    max-height: 240px;
    overflow-y: auto;
    font-size: 14px;
    color: #333333;
    line-height: 1.6;
    padding: 0;

    &.ql-blank::before {
      left: 0;
      right: 0;
      color: #949eaf;
      font-style: normal;
    }
  }
}

.editor-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  width: 100%;
  position: relative;
}

.toolbar-left {
  display: flex;
  align-items: center;
}

.toolbar-right {
  display: flex;
  align-items: center;
}

.fmt-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 15px;
  color: #333333;
  user-select: none;

  &:hover {
    background: #f0f0f0;
    border-radius: 4px;
  }
}

.fmt-bold {
  font-weight: 700;
}

.fmt-underline {
  text-decoration: underline;
}

.fmt-color {
  flex-direction: column;
  line-height: 1;

  .color-bar {
    width: 14px;
    height: 3px;
    margin-top: 2px;
    background: linear-gradient(
      90deg,
      #ff3333 0%,
      #ffcc00 33%,
      #00cc66 66%,
      #0066ff 100%
    );
    border-radius: 1px;
  }
}

.count-text {
  font-size: 12px;
  color: #999999;
  margin-right: 8px;
}

.count-over {
  color: #ff3333;
}

.clear-btn {
  padding: 0 6px;
  height: 18px;
  background: #ffffff;
  border: 1px solid #3a60ff;
  border-radius: 3px;
  font-size: 11px;
  text-align: center;
  color: #3a60ff;
  line-height: 17px;
  cursor: pointer;
  user-select: none;
}

.color-panel {
  position: absolute;
  top: 32px;
  // 锚定在颜色按钮（工具栏左起第 3 个，约 60px 处）旁边
  left: 56px;
  z-index: 10;
  background: #fff;
  border-radius: 5px;
  border: 1px solid #dcdde0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.color-grid {
  padding: 8px;
  display: grid;
  justify-content: flex-start;
  grid-template-columns: repeat(10, 1fr);
  grid-gap: 5px;
}

.color-item {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #dcdde0;
  cursor: pointer;
}

.color-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0);
  z-index: 9;
}
</style>
